import { useCallback, useMemo } from 'react';

import type { FilterOption } from '@shared/lib/types';
import { useAppDispatch, useAppSelector } from '@shared/redux';
import { viewFiltersActions, viewFiltersSelectors, type ViewScope } from '@shared/redux/ducks/view';
import type { DateFilterValue } from '@shared/ui/vanilla-calendar/lib/types';

import type { AnalyticsView, PeriodPreset } from '../ui/analytics-controls';

const ANALYTICS_PAGE = 'analytics';
const ACTIVE_VIEW_ID = 'activeView';

export type AnalyticsFiltersState = {
  periodPreset: PeriodPreset;
  periodValue: FilterOption<DateFilterValue>[];
  channelIds: number[];
  pageUrlPatterns: string[];
};

type PersistedAnalyticsView = {
  view?: AnalyticsView;
};

const buildScope = (organizationId: number, viewId: string): ViewScope => ({
  page: ANALYTICS_PAGE,
  organizationId,
  viewId,
});

const isAnalyticsView = (value: unknown): value is AnalyticsView =>
  value === 'overall' || value === 'widget';

const isPeriodPreset = (value: unknown): value is PeriodPreset =>
  value === 'today' || value === 'yesterday' || value === 'week' || value === 'month';

export const useAnalyticsView = (organizationId: number) => {
  const dispatch = useAppDispatch();
  const scope = useMemo(() => buildScope(organizationId, ACTIVE_VIEW_ID), [organizationId]);
  const persisted = useAppSelector(
    viewFiltersSelectors.selectFiltersByScope(scope),
  ) as PersistedAnalyticsView | null;
  const view = isAnalyticsView(persisted?.view) ? persisted.view : 'overall';

  const setView = useCallback(
    (nextView: AnalyticsView) => {
      dispatch(
        viewFiltersActions.updateFiltersForScope({
          scope,
          partialFilters: { view: nextView },
        }),
      );
    },
    [dispatch, scope],
  );

  return { view, setView };
};

export const useAnalyticsFilters = (
  organizationId: number,
  view: AnalyticsView,
  defaultPeriodValue: FilterOption<DateFilterValue>[],
) => {
  const dispatch = useAppDispatch();
  const scope = useMemo(() => buildScope(organizationId, view), [organizationId, view]);
  const persisted = useAppSelector(
    viewFiltersSelectors.selectFiltersByScope(scope),
  ) as Partial<AnalyticsFiltersState> | null;

  const filters = useMemo<AnalyticsFiltersState>(() => {
    const persistedChannelIds = Array.isArray(persisted?.channelIds)
      ? persisted.channelIds.filter((value) => Number.isFinite(value))
      : [];
    const periodValue =
      Array.isArray(persisted?.periodValue) && persisted.periodValue.length > 0
        ? persisted.periodValue
        : defaultPeriodValue;
    const pageUrlPatterns = Array.isArray(persisted?.pageUrlPatterns)
      ? Array.from(
          new Set(
            persisted.pageUrlPatterns
              .filter((value): value is string => typeof value === 'string')
              .map((value) => value.trim())
              .filter(Boolean),
          ),
        )
      : [];

    return {
      periodPreset: isPeriodPreset(persisted?.periodPreset) ? persisted.periodPreset : 'week',
      periodValue,
      channelIds: persistedChannelIds,
      pageUrlPatterns,
    };
  }, [defaultPeriodValue, persisted]);

  const updateFilters = useCallback(
    (partialFilters: Partial<AnalyticsFiltersState>) => {
      dispatch(
        viewFiltersActions.updateFiltersForScope({
          scope,
          partialFilters,
        }),
      );
    },
    [dispatch, scope],
  );

  return { filters, updateFilters };
};
